import { computed, makeObservable } from "mobx";
import { Direction } from "../../../lib/ContentSource";
import { describeEncodedSearchTermUsingFunctionNotationHtml } from "../../../lib/Search/EncodedSearchTerm";
import { EventSearchResults, EventSearchResult, NetworkRequestInformation } from "../../../lib/searchNetworkEvent";
import { getRegisteredDomain, urlToDomain, getUrlWithoutQueryOrHash } from "../../../lib/utils/domains";
import { mapGetOrCreate } from "../../../lib/utils/mapGetOrCreate";
import { SearchResultsObserver } from "../SearchResultsObserver";
import { ProcessInfo } from "../../../State/ProcessInfo";


export class IpReportData {
	constructor(
		protected readonly searchResultObserver: SearchResultsObserver,
		protected readonly ipAddress?: string
	) {
		makeObservable(this);
	}

	@computed get networkEventsWithRequestInformationAndSearchResults() {
		const results = [] as EventSearchResults[];
		const {ipAddress} = this;
		for (const entry of this.searchResultObserver.hasSearchResultsWithNetworkRequestInformation) {
			const { searchResults: searchResultsForAllTerms, ...rest } = entry;
			const searchResults = searchResultsForAllTerms.filter( s => s.originalDataType.startsWith('ip') && (ipAddress == null || ipAddress.length === 0 || s.searchTermAsSpecified === ipAddress));
			if (searchResults.length > 0) {
				results.push({searchResults, ...rest} as EventSearchResults);
			}
		}
		ProcessInfo.log(`SearchResultsObserver.forIpAddress ${ipAddress ?? "none"} yielded ${results.length} results`);
		return results;
	}

	@computed get summary() {
		const bySourceRegisteredDomainThenDirectionThenDestinationDomain = new Map<string /* source registered domain */,
			Map<Direction, Set<string /* destination domain */>
			>>();

		for (const record of this.networkEventsWithRequestInformationAndSearchResults) {
			const sourceRegisteredDomain = getRegisteredDomain(record.originUrl);
			const destinationDomain = urlToDomain(record.destinationUrl);
			if (sourceRegisteredDomain == null || destinationDomain == null) continue;

			const bySourceRegisteredDomain = mapGetOrCreate(bySourceRegisteredDomainThenDirectionThenDestinationDomain, sourceRegisteredDomain, () => new Map());
			for (const searchResult of record.searchResults) {
				const byDirection = mapGetOrCreate(bySourceRegisteredDomain, searchResult.direction, () => new Set());
				byDirection.add(destinationDomain);
			}
		}

		return [...bySourceRegisteredDomainThenDirectionThenDestinationDomain.entries()].map(([pageRegisteredDomain, byDirection]) => ({
			pageRegisteredDomain,
			directions: [...byDirection.entries()].map(([direction, destinationDomainSet]) => ({
				direction, destinationDomains: [...destinationDomainSet].sort()
			})).sort((a, b) => a.direction.localeCompare(b.direction)), // received before sent
		})).sort((a, b) => a.pageRegisteredDomain.localeCompare(b.pageRegisteredDomain));
	}

	@computed get byDestination() {
		console.log("SearchResultsObserver.forIpAddress", this.ipAddress);
		const byDestinationDomainThenPageUrlThenDirectionThenEncoding = new Map<string /* destination url without query or path */,
			Map<string /* source page url without query or path */, Map<Direction, Map<string /* encoding string */, EventSearchResult[]
			>>>>();

		for (const record of this.networkEventsWithRequestInformationAndSearchResults) {
			const sourcePageUrlTrimmed = getUrlWithoutQueryOrHash(record.originUrl);
			const destinationDomain = urlToDomain(record.destinationUrl);
			if (sourcePageUrlTrimmed == null || destinationDomain == null) continue;

			const byDestinationPage = mapGetOrCreate(byDestinationDomainThenPageUrlThenDirectionThenEncoding, destinationDomain, () => new Map());
			const bySourcePage = mapGetOrCreate(byDestinationPage, sourcePageUrlTrimmed, () => new Map());
			const { searchResults: _, ...hasNetworkEventAndRequestInformation } = record;
			for (const searchResult of record.searchResults) {
				const encodingString = describeEncodedSearchTermUsingFunctionNotationHtml(searchResult);
				const byDirection = mapGetOrCreate(bySourcePage, searchResult.direction, () => new Map());
				const byEncoding = mapGetOrCreate(byDirection, encodingString, () => []);
				byEncoding.push({ searchResult, ...(hasNetworkEventAndRequestInformation satisfies NetworkRequestInformation)} satisfies EventSearchResult);
			}
		}

		return [...byDestinationDomainThenPageUrlThenDirectionThenEncoding.entries()].map(([destinationDomain, byDestinationMap]) => {
			const pages = [...byDestinationMap.entries()].map(([pageUrl, byDirectionMap]) => {
				const directions = [...byDirectionMap.entries()].map(([direction, byEncodingMap]) => {
					const encodings = [...byEncodingMap.entries()].map(([encodingString, eventsAndResults]) => {
						const inferredTypeNames = [...new Set<string>(eventsAndResults.map(({ searchResult }) => searchResult.inferredDataTypeName).filter(s => s != null))].toSorted();
						return { encodingString, eventsAndResults, inferredTypeNames };
					}).sort((a, b) => a.encodingString.localeCompare(b.encodingString));
					const inferredTypeNames = [...new Set<string>(([] as string[]).concat(...encodings.map(e => e.inferredTypeNames)))].toSorted();
					return { direction, encodings, inferredTypeNames };
				}).sort((a, b) => a.direction.localeCompare(b.direction)); // received before sent
				const inferredTypeNames = [...new Set<string>(([] as string[]).concat(...directions.map(e => e.inferredTypeNames)))].toSorted();
				return { pageUrl, directions, inferredTypeNames };
			}).sort((a, b) => a.pageUrl.localeCompare(b.pageUrl));
			const inferredTypeNames = [...new Set<string>(([] as string[]).concat(...pages.map(e => e.inferredTypeNames)))].toSorted();
			return { destinationDomain, pages, inferredTypeNames };
		}).sort((a, b) => a.destinationDomain.localeCompare(b.destinationDomain));
	};
}
